export type PulsarStream = 'all' | 'agent' | 'iot' | 'api';

export type PulsarFilters = {
  stream: PulsarStream;
  q: string;
  app_id: string;
  request_id: string;
  agent_id: string;
  workflow: string;
  tool_name: string;
  device_id: string;
  api_id: string;
  endpoint_id: string;
  provider_key: string;
  since: string;
  until: string;
  limit: number;
};

function isoForDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function toIso(localValue: string): string | null {
  if (!localValue) return null;
  const d = new Date(localValue);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toISOString();
}

export function buildFilterParams(filters: PulsarFilters, offset = 0): string {
  const p = new URLSearchParams();
  p.set('stream', filters.stream);
  p.set('limit', String(filters.limit));
  p.set('offset', String(offset));
  const sinceIso = toIso(filters.since);
  const untilIso = toIso(filters.until);
  if (sinceIso) p.set('since', sinceIso);
  if (untilIso) p.set('until', untilIso);

  const optional: Array<keyof Omit<PulsarFilters, 'stream' | 'since' | 'until' | 'limit'>> = [
    'q', 'app_id', 'request_id', 'agent_id', 'workflow', 'tool_name',
    'device_id', 'api_id', 'endpoint_id', 'provider_key',
  ];
  for (const key of optional) {
    const value = String(filters[key] || '').trim();
    if (value) p.set(key, value);
  }
  return p.toString();
}

export function buildConversationPath(requestId: string, limit = 500): string {
  return `/pulsar/conversations/${encodeURIComponent(requestId)}?limit=${limit}`;
}

export function buildEventsExportPath(filters: PulsarFilters, format: 'json' | 'csv'): string {
  const qs = buildFilterParams({ ...filters, limit: Math.min(filters.limit, 200) }, 0);
  return `/cp/pulsar/events/export?format=${format}&${qs}`;
}

export function buildConversationExportPath(requestId: string, format: 'json' | 'csv'): string {
  return `/cp/pulsar/conversations/${encodeURIComponent(requestId)}/export?format=${format}&limit=1000`;
}

export function defaultFilters(): PulsarFilters {
  return {
    stream: 'all',
    q: '',
    app_id: '',
    request_id: '',
    agent_id: '',
    workflow: '',
    tool_name: '',
    device_id: '',
    api_id: '',
    endpoint_id: '',
    provider_key: '',
    since: isoForDatetimeLocal(new Date(Date.now() - (24 * 60 * 60 * 1000))),
    until: isoForDatetimeLocal(new Date()),
    limit: 50,
  };
}

